import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Grid } from "@mui/material";
import { useForm } from "react-hook-form";
import FormTextField from "../../../components/textField/formTextField";
import LaddaButton from "../../../components/buttons/laddaButton";
import SimpleTable from "../../../components/tables/simpleTable";
import { technicalFeaturesColumns } from "../columns";
import { icons } from "../../../constants";
import {
  callActionApiAsync,
  callApiAsync,
  getBiomedicalEquipmentModelFeatureData,
  getBiomedicalEquipmentModelResume,
  postDeleteBiomedicalEquipmentModelFeature,
  putSaveBiomedicalEquipmentModelResume
} from "../../../services/apiService";
import ModelFeatureModal from "./ModelFeatureModal";
import VerificationModal from "../../../components/modals/verificationModal";

const defaultValues = {
  originPlace: "",
  warranty: "",
  mainTechnology: "",
  recommendations: ""
};

const ModelResumeTab = ({
  modelRow,
  userId,
  isCatalogAdmin,
  pushToast,
  equipmentCount
}) => {
  const [featureRows, setFeatureRows] = useState([]);
  const [featureOptions, setFeatureOptions] = useState([]);
  const [loading, setLoading] = useState(false);
  const [openFeatureModal, setOpenFeatureModal] = useState(false);
  const [selectedFeatureRow, setSelectedFeatureRow] = useState(null);
  const [openSaveVerification, setOpenSaveVerification] = useState(false);
  const [pendingFormData, setPendingFormData] = useState(null);
  const [rowPendingDelete, setRowPendingDelete] = useState(null);
  const [openDeleteVerification, setOpenDeleteVerification] = useState(false);

  const { control, handleSubmit, reset } = useForm({
    defaultValues
  });

  const selectedModelId = modelRow?.value ? Number(modelRow.value) : null;
  const affectedEquipmentCount = Number(equipmentCount ?? 0);

  const loadResumeTemplate = useCallback(() => {
    if (!selectedModelId) {
      reset(defaultValues);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelResume(selectedModelId),
      (data) => {
        reset({
          originPlace: data?.originPlace || "",
          warranty: data?.warranty || "",
          mainTechnology: data?.mainTechnology || "",
          recommendations: data?.recommendations || ""
        });
      }
    );
  }, [reset, selectedModelId]);

  const loadFeatureData = useCallback(() => {
    if (!selectedModelId || !userId) {
      setFeatureRows([]);
      setFeatureOptions([]);
      return;
    }

    callApiAsync(
      () => getBiomedicalEquipmentModelFeatureData(selectedModelId, userId),
      (data) => {
        setFeatureRows(data?.table || []);
        setFeatureOptions(data?.list || []);
      }
    );
  }, [selectedModelId, userId]);

  useEffect(() => {
    if (!selectedModelId) {
      reset(defaultValues);
      setFeatureRows([]);
      setFeatureOptions([]);
      return;
    }

    loadResumeTemplate();
    loadFeatureData();
  }, [loadFeatureData, loadResumeTemplate, reset, selectedModelId]);

  const saveModelResumeTemplate = async (formData) => {
    if (!selectedModelId || !userId) return false;
    let saveSucceeded = false;

    await callActionApiAsync(
      () =>
        putSaveBiomedicalEquipmentModelResume({
          userId,
          modelId: selectedModelId,
          originPlace: formData.originPlace,
          warranty: formData.warranty,
          mainTechnology: formData.mainTechnology,
          recommendations: formData.recommendations
        }),
      (data) => {
        loadFeatureData();
        pushToast(
          "success",
          "Hoja de vida por modelo",
          `Se guardo la plantilla del modelo y se sobrescribieron ${
            data?.updatedEquipment ?? 0
          } hoja(s) de vida de equipos asociados.`
        );
        saveSucceeded = true;
      },
      (error) => {
        pushToast(
          "error",
          "Hoja de vida por modelo",
          typeof error === "string" && error
            ? error
            : "No se pudo guardar la hoja de vida del modelo."
        );
      },
      setLoading
    );

    return saveSucceeded;
  };

  const onSubmit = (formData) => {
    if (!selectedModelId || !userId) return;
    setPendingFormData(formData);
    setOpenSaveVerification(true);
  };

  const handleCloseSaveVerification = () => {
    if (loading) return;
    setOpenSaveVerification(false);
    setPendingFormData(null);
  };

  const handleConfirmSaveVerification = async () => {
    if (!pendingFormData) return;
    const saveSucceeded = await saveModelResumeTemplate(pendingFormData);
    if (!saveSucceeded) return;
    setOpenSaveVerification(false);
    setPendingFormData(null);
  };

  const handleRequestDeleteFeature = useCallback(
    (row) => {
      if (!row?.id || !userId) return;
      setRowPendingDelete(row);
      setOpenDeleteVerification(true);
    },
    [userId]
  );

  const handleCloseDeleteVerification = useCallback(() => {
    setOpenDeleteVerification(false);
    setRowPendingDelete(null);
  }, []);

  const handleConfirmDeleteFeature = useCallback(async () => {
    if (!rowPendingDelete?.id || !userId) return;

    await callActionApiAsync(
      () =>
        postDeleteBiomedicalEquipmentModelFeature({
          id: rowPendingDelete.id,
          userId
        }),
      (data) => {
        loadFeatureData();
        handleCloseDeleteVerification();
        pushToast(
          "success",
          "Caracteristicas tecnicas",
          `La plantilla tecnica del modelo se actualizo y se sobrescribieron las caracteristicas de ${
            data?.updatedEquipment ?? 0
          } equipo(s).`
        );
      },
      (error) => {
        pushToast(
          "error",
          "Caracteristicas tecnicas",
          typeof error === "string" && error
            ? error
            : "No se pudo eliminar la caracteristica del modelo."
        );
      }
    );
  }, [
    handleCloseDeleteVerification,
    loadFeatureData,
    pushToast,
    rowPendingDelete,
    userId
  ]);

  const tableButtons = useMemo(
    () =>
      isCatalogAdmin
        ? [
            {
              type: "icon",
              icon: icons.editYellow,
              onClick: (row) => {
                setSelectedFeatureRow(row);
                setOpenFeatureModal(true);
              }
            },
            {
              type: "icon",
              icon: icons.trash,
              onClick: handleRequestDeleteFeature
            }
          ]
        : [],
    [handleRequestDeleteFeature, isCatalogAdmin]
  );

  if (!selectedModelId) {
    return (
      <div className="admin-catalog-detail-card">
        <p className="admin-catalog-detail-value admin-catalog-hint">
          Seleccione una marca y un modelo para editar su hoja de vida base y
          sus caracteristicas tecnicas.
        </p>
      </div>
    );
  }

  return (
    <>
      <div className="admin-catalog-detail-card">
        <div className="HContainer j-between">
          <div className="mr-20 IV">
            <p className="admin-catalog-detail-label">Modelo seleccionado</p>
            <p className="admin-catalog-detail-value">
              {modelRow?.name || "-"}
            </p>
          </div>
          <div>
            <p className="admin-catalog-detail-label">
              Equipos asociados al modelo
            </p>
            <p className="admin-catalog-detail-value">
              {affectedEquipmentCount}
            </p>
          </div>
        </div>
        <p className="admin-catalog-hint">
          Cada cambio en esta plantilla sobrescribe los campos de hoja de vida
          de todos los equipos asociados a este modelo. Los nuevos equipos
          tambien tomaran estos valores como base.
        </p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)}>
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <p className="create-label">Lugar de origen:</p>
            <FormTextField
              name="originPlace"
              control={control}
              label=""
              margin={"0"}
              disabled={!isCatalogAdmin}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label">Garantia:</p>
            <FormTextField
              name="warranty"
              control={control}
              label=""
              margin={"0"}
              disabled={!isCatalogAdmin}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label">Tecnologia predominante:</p>
            <FormTextField
              name="mainTechnology"
              control={control}
              label=""
              margin={"0"}
              disabled={!isCatalogAdmin}
            />
          </Grid>
          <Grid item xs={12} md={6}>
            <p className="create-label">
              Requisitos y recomendaciones del fabricante:
            </p>
            <FormTextField
              name="recommendations"
              control={control}
              label=""
              isTextArea
              margin={"0"}
              disabled={!isCatalogAdmin}
            />
          </Grid>
          {isCatalogAdmin && (
            <Grid item xs={12} className="HContainer j-center mt-10">
              <LaddaButton type="submit" variant="contained" disabled={loading}>
                Guardar hoja de vida del modelo
              </LaddaButton>
            </Grid>
          )}
        </Grid>
      </form>

      <div className="mt-25">
        <div className="IHContainer j-space-between mb-15">
          <p className="createRist-title ml-0">
            Caracteristicas tecnicas del modelo
          </p>
          {isCatalogAdmin && (
            <LaddaButton
              width={"40px"}
              fontSize={"12px"}
              height={"40px"}
              variant="contained"
              margin={"0"}
              onClick={() => {
                setSelectedFeatureRow(null);
                setOpenFeatureModal(true);
              }}
            >
              <img className="btn-icon" src={icons.plusIcon} alt="icon" />
            </LaddaButton>
          )}
        </div>

        <SimpleTable
          data={featureRows}
          columns={technicalFeaturesColumns}
          scrollHeight={"280px"}
          text={"Caracteristicas tecnicas del modelo"}
          buttons={tableButtons}
        />
      </div>

      <ModelFeatureModal
        open={openFeatureModal}
        close={() => {
          setOpenFeatureModal(false);
          setSelectedFeatureRow(null);
        }}
        selectedRow={selectedFeatureRow}
        modelId={selectedModelId}
        userId={userId}
        featureOptions={featureOptions}
        setFeatureOptions={setFeatureOptions}
        affectedEquipmentCount={affectedEquipmentCount}
        onSaved={(data) => {
          loadFeatureData();
          pushToast(
            "success",
            "Caracteristicas tecnicas",
            `La plantilla tecnica del modelo se actualizo y se sobrescribieron las caracteristicas de ${
              data?.updatedEquipment ?? 0
            } equipo(s).`
          );
        }}
      />

      <VerificationModal
        open={openSaveVerification}
        handleClose={handleCloseSaveVerification}
        alertText={
          affectedEquipmentCount > 0
            ? `Esta accion sobrescribira la hoja de vida de ${affectedEquipmentCount} equipo(s) asociado(s) a este modelo y tambien quedara como base para los nuevos equipos. Desea continuar?`
            : "Esta plantilla quedara como base para los nuevos equipos de este modelo. Actualmente no hay equipos asociados para sobrescribir. Desea continuar?"
        }
        onConfirm={handleConfirmSaveVerification}
        onCancel={handleCloseSaveVerification}
        iconSeverity="warning"
        yesNo
        loading={loading}
      />

      <VerificationModal
        open={openDeleteVerification}
        handleClose={handleCloseDeleteVerification}
        alertText={
          affectedEquipmentCount > 0
            ? `Esta accion eliminara la caracteristica "${
                rowPendingDelete?.featureName || ""
              }" de la plantilla y sobrescribira las caracteristicas tecnicas de ${affectedEquipmentCount} equipo(s) asociado(s) a este modelo. Desea continuar?`
            : `Esta accion eliminara la caracteristica "${
                rowPendingDelete?.featureName || ""
              }" de la plantilla. Actualmente no hay equipos asociados para sobrescribir. Desea continuar?`
        }
        onConfirm={handleConfirmDeleteFeature}
        onCancel={handleCloseDeleteVerification}
        iconSeverity="warning"
        yesNo
      />
    </>
  );
};

export default ModelResumeTab;
